// src/services/reportItems.js
// ---------------------------
// The Reports screen's "Declined & Needs Info items" report (5 Oct 2026): ONE ROW
// PER ITEM that is Declined or Needs Info, with the responder's reason. Pure, so it
// is unit-tested and feeds both the on-screen preview and the CSV download.
//
// Every field comes from the request already loaded on the Reports screen (the
// child tree via get_request_children, which returns `reason` since db-148), so
// there is no extra query. Matches the SQL David ran on prod on 5 Oct 2026.
//
// "Contact Name" is the request's contact; requests.submitted_by_name is never
// written by any request path, so there is no caseworker name to show.

import { businessDay } from "../utils/businessDate";
import { APPROVAL_STATUSES } from "../utils/enums";
import { calcUniItems } from "../utils/formConstants";

export const ITEM_REPORT_HEADERS = [
  "Ref No",
  "Date",
  "DFV Service Code",
  "DFV Service",
  "State",
  "Contact Name",
  "Request Status",
  "Child",
  "Year Level",
  "School",
  "Category",
  "Detail",
  "Decision",
  "Requested $",
  "Item $",
  "Reason",
  "Decided On",
];

// Column indexes holding numbers (right-aligned in the preview).
export const ITEM_REPORT_NUMERIC_COLS = [7, 13, 14];

const INCLUDED = [APPROVAL_STATUSES.DECLINED, APPROVAL_STATUSES.NEEDS_INFO];

// The Reports Category filter value -> this report's category label.
const CATEGORY_OF = { fees: "Fee", uniforms: "Uniforms", shoes: "Shoes", laptops: "Device" };

function num(v) {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

// 2dp, or blank when there is no value (e.g. requested_amount on an old row).
function money2(v) {
  if (v == null || v === "") return "";
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
}

// Every item on one child, normalised to { category, detail, item, amount }.
function childItems(c) {
  const out = [];
  for (const f of c.fees || []) {
    out.push({
      category: "Fee",
      detail: f.feeDesc ? `${f.feeType || ""}: ${f.feeDesc}` : f.feeType || "",
      item: f,
      amount: num(f.feeAmount),
    });
  }
  if (c.uniforms) {
    out.push({
      category: "Uniforms",
      detail: c.uniforms.supplier || "",
      item: c.uniforms,
      amount: calcUniItems([c.uniforms]),
    });
  }
  if (c.shoes) {
    const s = c.shoes;
    out.push({
      category: "Shoes",
      detail: `Joggers x${s.jQty || 0}, Leather x${s.lQty || 0}`,
      item: s,
      amount: (s.jQty || 0) * num(s.jCost) + (s.lQty || 0) * num(s.lCost),
    });
  }
  if (c.laptop) {
    out.push({
      category: "Device",
      detail: c.laptop.deviceType || "",
      item: c.laptop,
      amount: num(c.laptop.estimatedCost),
    });
  }
  return out;
}

/**
 * Header row + one row per Declined / Needs Info item, as raw cell values.
 * `category` is the Reports Category filter ("all" | "fees" | "uniforms" |
 * "shoes" | "laptops"); when set, only that category's items are listed.
 */
export function buildItemReportTable(rows, category = "all") {
  const table = [ITEM_REPORT_HEADERS];
  for (const r of rows || []) {
    (r.children || []).forEach((c, ci) => {
      for (const x of childItems(c)) {
        if (!INCLUDED.includes(x.item.approvalStatus)) continue;
        if (category !== "all" && CATEGORY_OF[category] !== x.category) continue;
        table.push([
          r.refNo,
          r.reportDay || r.date,
          r.shelterCode,
          r.shelterName,
          r.state,
          r.contactName,
          r.status,
          ci + 1,
          c.yearLevel,
          c.schoolName,
          x.category,
          x.detail,
          x.item.approvalStatus,
          money2(x.item.requestedAmount),
          x.amount.toFixed(2),
          x.item.reason,
          businessDay(x.item.confirmedAt),
        ]);
      }
    });
  }
  return table;
}
